import { useLingui } from "@lingui/react/macro";
import type { Bot, ComputerStatus, Group } from "@rakazo/contracts";
import { createScreenReconnects, SCREEN_RECONNECT_DELAY_MS } from "@rakazo/core";
import {
  BotAvatar,
  Button,
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@rakazo/ui-web";
import { ChevronLeft, ChevronRight, Maximize2, RefreshCw } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  embeddableScreenUrl,
  screenFrameMessage,
  screenIframeSandbox,
} from "../../lib/computer-screen";
import type { createSpaceRpc } from "../../lib/rpc";
import { errorText } from "../../lib/user-error";
import { computerLabel, computerPlaceholder } from "./computer-view";

export type CompanyComputerEntry = {
  key: string;
  kind: "team" | "dedicated";
  /** The stable handle used to read the computer's status and screen. */
  bot: Bot;
};

const STATUS_POLL_MS = 2000;

type SelectedComputerStatus = {
  value: ComputerStatus | null;
  failed: boolean;
  retry: () => void;
};

/**
 * Every employee's computer access, one entry each. Team-mode employees share the same physical
 * Team Computer, but their leases and screen controls are per bot, so each entry has its own state.
 */
export function useCompanyComputers({
  rpc,
  bots,
  group,
  enabled,
}: {
  rpc: ReturnType<typeof createSpaceRpc>;
  bots: Bot[];
  group: Group | undefined;
  enabled: boolean;
}) {
  const memberBots = useMemo(() => {
    if (!group) return [];
    const ids = new Set(group.members.map((member) => member.botId));
    return bots.filter((bot) => ids.has(bot.id));
  }, [bots, group]);
  const [statuses, setStatuses] = useState<Record<string, ComputerStatus | null>>({});
  const [failures, setFailures] = useState<Record<string, boolean>>({});
  const requestVersions = useRef<Record<string, number>>({});
  const inFlight = useRef<Record<string, number>>({});
  const retryStatus = useRef<(botId: string) => void>(() => undefined);
  const statusKey = memberBots.map((bot) => bot.id).join("|");

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const ids = statusKey ? statusKey.split("|") : [];
    async function load(botId: string, retry = false) {
      // A slow response must finish instead of being superseded by every poll.
      if (inFlight.current[botId] && !retry) return;
      const version = (requestVersions.current[botId] ?? 0) + 1;
      requestVersions.current[botId] = version;
      inFlight.current[botId] = version;
      try {
        const status = await rpc.computer.status({ botId });
        if (cancelled || requestVersions.current[botId] !== version) return;
        setStatuses((current) => ({ ...current, [botId]: status }));
        setFailures((current) => ({ ...current, [botId]: false }));
      } catch {
        if (cancelled || requestVersions.current[botId] !== version) return;
        // Keep the last known state and offer a retry instead of showing a stopped computer.
        setFailures((current) => ({ ...current, [botId]: true }));
      } finally {
        if (inFlight.current[botId] === version) delete inFlight.current[botId];
      }
    }
    retryStatus.current = (botId) => {
      if (ids.includes(botId)) void load(botId, true);
    };
    for (const botId of ids) void load(botId);
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") for (const botId of ids) void load(botId);
    }, STATUS_POLL_MS);
    return () => {
      cancelled = true;
      retryStatus.current = () => undefined;
      for (const botId of ids) delete inFlight.current[botId];
      window.clearInterval(timer);
    };
  }, [enabled, rpc, statusKey]);

  const entries = useMemo<CompanyComputerEntry[]>(
    () =>
      memberBots.map((bot) => ({
        key: bot.id,
        kind: bot.computerMode,
        bot,
      })),
    [memberBots],
  );
  const [selectedKey, setSelectedKey] = useState("");

  useEffect(() => {
    if (!entries.some((entry) => entry.key === selectedKey)) {
      setSelectedKey(entries[0]?.key ?? "");
    }
  }, [entries, selectedKey]);

  const selectedEntry = entries.find((entry) => entry.key === selectedKey) ?? entries[0];
  const selectedStatus: SelectedComputerStatus = {
    value: selectedEntry ? (statuses[selectedEntry.bot.id] ?? null) : null,
    failed: selectedEntry ? (failures[selectedEntry.bot.id] ?? false) : false,
    retry: () => {
      if (selectedEntry) retryStatus.current(selectedEntry.bot.id);
    },
  };

  function setStatus(botId: string, status: ComputerStatus) {
    // Boot is newer than every status request already in flight for this bot.
    requestVersions.current[botId] = (requestVersions.current[botId] ?? 0) + 1;
    delete inFlight.current[botId];
    setStatuses((current) => ({ ...current, [botId]: status }));
    setFailures((current) => ({ ...current, [botId]: false }));
  }

  return {
    entries,
    statuses,
    selectedEntry,
    selectedStatus,
    selectedKey,
    setSelectedKey,
    setStatus,
  };
}

function stateLabel(
  status: ComputerStatus | null,
  userHasControl: boolean,
  modeLabel: string,
  t: ReturnType<typeof useLingui>["t"],
) {
  const state = userHasControl
    ? t`You have control`
    : status?.state === "running"
      ? t`Running`
      : status?.state === "booting"
        ? t`Starting…`
        : status?.state === "suspended"
          ? t`Asleep`
          : status?.state === "error"
            ? t`Computer failed to boot`
            : status
              ? t`Computer is stopped`
              : null;
  return state ? `${modeLabel} · ${state}` : modeLabel;
}

/** The computers as a header tab strip; the arrows move the selection. */
export function CompanyComputerTabs({
  entries,
  statuses,
  selectedKey,
  onSelect,
}: {
  entries: readonly CompanyComputerEntry[];
  statuses: Record<string, ComputerStatus | null>;
  selectedKey: string;
  onSelect: (key: string) => void;
}) {
  const { t } = useLingui();
  const selectedRef = useRef<HTMLButtonElement>(null);
  const selectedIndex = Math.max(
    0,
    entries.findIndex((entry) => entry.key === selectedKey),
  );

  useEffect(() => {
    selectedRef.current?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
  }, [selectedKey]);

  function move(delta: number) {
    if (entries.length < 2) return;
    const next = entries[(selectedIndex + delta + entries.length) % entries.length];
    if (next) onSelect(next.key);
  }

  return (
    <div className="app-no-drag flex min-w-0 flex-1 items-center gap-1">
      <div
        role="tablist"
        aria-label={t`Computers`}
        className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden"
      >
        {entries.map((entry) => {
          const status = statuses[entry.bot.id];
          const userHasControl =
            status?.controlHolder === "user" && status.controlBotId === entry.bot.id;
          const busy = Boolean(status?.busyBotName || userHasControl);
          const selected = entry.key === selectedKey;
          const modeLabel = entry.kind === "team" ? t`Team Computer` : t`Computer`;
          const label = stateLabel(status ?? null, userHasControl, modeLabel, t);
          return (
            <button
              key={entry.key}
              ref={selected ? selectedRef : undefined}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-label={`${entry.bot.name}, ${label}`}
              data-testid={`company-computer-tab-${entry.key}`}
              onClick={() => onSelect(entry.key)}
              className={`flex h-8 min-w-0 max-w-[220px] items-center gap-2 rounded-md px-2 text-start transition-colors ${
                selected ? "bg-selected" : "hover:bg-hover"
              }`}
            >
              <span className="relative shrink-0">
                <BotAvatar
                  avatarSet={entry.bot.avatarSet}
                  color={entry.bot.color}
                  identity={entry.bot.id}
                  size={20}
                  status={entry.bot.status}
                />
                {busy ? (
                  <span
                    aria-hidden="true"
                    className="absolute -end-0.5 bottom-0 size-2 rounded-full bg-success ring-2 ring-background"
                  />
                ) : null}
              </span>
              <span className="min-w-0">
                <span className="block truncate text-ui-sm text-foreground" dir="auto">
                  {entry.bot.name}
                </span>
                <span className="block truncate text-ui-2xs text-foreground-subtle">{label}</span>
              </span>
            </button>
          );
        })}
      </div>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={t`Previous computer`}
        disabled={entries.length < 2}
        onClick={() => move(-1)}
      >
        <ChevronLeft size={16} strokeWidth={1.8} />
      </Button>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={t`Next computer`}
        disabled={entries.length < 2}
        onClick={() => move(1)}
      >
        <ChevronRight size={16} strokeWidth={1.8} />
      </Button>
    </div>
  );
}

export function CompanyComputerPanel({
  rpc,
  entries,
  selectedEntry,
  selectedStatus,
  onStatusChange,
}: {
  rpc: ReturnType<typeof createSpaceRpc>;
  entries: readonly CompanyComputerEntry[];
  selectedEntry: CompanyComputerEntry | undefined;
  selectedStatus: SelectedComputerStatus;
  onStatusChange: (botId: string, status: ComputerStatus) => void;
}) {
  const { t } = useLingui();
  const { value: status, failed: statusFailed, retry: retryStatus } = selectedStatus;
  const [screenUrl, setScreenUrl] = useState<string | null>(null);
  const [screenLoading, setScreenLoading] = useState(false);
  const [waking, setWaking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [screenFrameKey, setScreenFrameKey] = useState(0);
  const [enlarged, setEnlarged] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const enlargedFrameRef = useRef<HTMLIFrameElement>(null);
  const reconnectTimer = useRef<number | null>(null);
  const screenReconnects = useRef(createScreenReconnects());

  const selectedBotId = selectedEntry?.bot.id;
  const selectedLabel = selectedEntry
    ? computerLabel(selectedEntry.kind, selectedEntry.bot.name)
    : null;

  useEffect(() => {
    setScreenUrl(null);
    setError(null);
    if (!selectedBotId || status?.state !== "running") {
      setScreenLoading(false);
      return;
    }
    let cancelled = false;
    setScreenLoading(true);
    void rpc.computer
      .screenUrl({ botId: selectedBotId })
      .then((screen) => {
        if (!cancelled) setScreenUrl(embeddableScreenUrl(screen.url));
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(errorText(cause));
      })
      .finally(() => {
        if (!cancelled) setScreenLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [rpc, selectedBotId, status?.state, refreshKey]);

  // A visible company computer is an active viewer, just like the single-bot computer panel:
  // without this the idle sweep suspends it after ten minutes and the framed screen goes black.
  useEffect(() => {
    if (!selectedBotId || status?.state !== "running") return;
    const ping = () => {
      if (document.visibilityState !== "visible") return;
      void rpc.computer.heartbeat({ botId: selectedBotId }).catch(() => undefined);
    };
    ping();
    const timer = window.setInterval(ping, 60_000);
    const onVisibility = () => {
      if (document.visibilityState === "visible") ping();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [rpc, selectedBotId, status?.state]);

  useEffect(() => {
    if (!selectedBotId || !screenUrl) return;
    screenReconnects.current.for(selectedBotId).loaded();
  }, [selectedBotId, screenUrl, screenFrameKey]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const frame = iframeRef.current;
      const enlarged = enlargedFrameRef.current;
      if (
        (!frame || event.source !== frame.contentWindow) &&
        (!enlarged || event.source !== enlarged.contentWindow)
      )
        return;
      if (!selectedBotId) return;
      const message = screenFrameMessage(event.data);
      if (!message) return;
      const action = screenReconnects.current.for(selectedBotId).report(message.clvabotScreen);
      if (action === "reconnect") {
        if (reconnectTimer.current !== null) window.clearTimeout(reconnectTimer.current);
        reconnectTimer.current = window.setTimeout(() => {
          setScreenUrl(null);
          setRefreshKey((key) => key + 1);
          setScreenFrameKey((key) => key + 1);
        }, SCREEN_RECONNECT_DELAY_MS);
        return;
      }
      if (message.clvabotScreen === "failed" || message.clvabotScreen === "disconnected") {
        setError(
          message.clvabotScreen === "disconnected"
            ? t`The screen connection closed.`
            : t`Could not connect to the computer screen`,
        );
      }
    };
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
      if (reconnectTimer.current !== null) window.clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    };
  }, [selectedBotId, t]);

  function refreshScreen() {
    setRefreshKey((key) => key + 1);
    setScreenFrameKey((key) => key + 1);
  }

  async function wake() {
    if (!selectedEntry || waking) return;
    setWaking(true);
    setError(null);
    try {
      const status = await rpc.computer.boot({ botId: selectedEntry.bot.id });
      onStatusChange(selectedEntry.bot.id, status);
      setRefreshKey((key) => key + 1);
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      setWaking(false);
    }
  }

  if (!entries.length) return null;

  const canBoot =
    !waking &&
    !statusFailed &&
    (status?.state === "stopped" || status?.state === "suspended" || status?.state === "error");
  const showScreen = status?.state === "running" && screenUrl !== null;
  const loading = (!status && !statusFailed) || screenLoading;
  const statusFailure = (
    <div
      role="alert"
      className="flex items-center justify-center gap-2 text-ui-sm text-destructive"
    >
      <span>{t`Could not load computer status`}</span>
      <Button variant="secondary" size="sm" onClick={retryStatus}>
        {t`Retry`}
      </Button>
    </div>
  );

  return (
    <>
      <section data-testid="company-computer" className="mb-6">
        <ContextMenu>
          <ContextMenuTrigger
            render={<div className="relative aspect-[16/10] overflow-hidden bg-black" />}
          >
            {loading ? (
              <div className="size-full animate-pulse bg-muted" />
            ) : showScreen ? (
              <iframe
                key={`${selectedBotId ?? ""}:${screenFrameKey}`}
                ref={iframeRef}
                title={selectedLabel ?? t`Computer`}
                src={screenUrl}
                sandbox={screenIframeSandbox(screenUrl)}
                className="size-full border-0 bg-black"
                allow="clipboard-read; clipboard-write"
                style={{ pointerEvents: "none" }}
              />
            ) : (
              <div className="grid size-full place-items-center gap-2 px-4 text-center text-ui-base text-muted-foreground">
                {statusFailed && !status ? (
                  statusFailure
                ) : (
                  <>
                    <span>
                      {waking
                        ? t`Starting…`
                        : computerPlaceholder(status?.state, false, selectedLabel ?? t`Computer`)}
                    </span>
                    {canBoot ? (
                      <Button variant="secondary" size="sm" onClick={() => void wake()}>
                        {status?.state === "error" ? t`Retry` : t`Wake`}
                      </Button>
                    ) : null}
                  </>
                )}
              </div>
            )}
          </ContextMenuTrigger>
          <ContextMenuContent>
            <ContextMenuItem onClick={refreshScreen}>
              <RefreshCw aria-hidden="true" />
              {t`Refresh`}
            </ContextMenuItem>
            <ContextMenuItem disabled={!showScreen} onClick={() => setEnlarged(true)}>
              <Maximize2 aria-hidden="true" />
              {t`Open in full window`}
            </ContextMenuItem>
          </ContextMenuContent>
        </ContextMenu>
        {statusFailed && status ? <div className="mt-2">{statusFailure}</div> : null}
        {error ? <p className="mt-2 text-ui-sm text-destructive">{error}</p> : null}
      </section>
      <Dialog open={enlarged} onOpenChange={setEnlarged}>
        <DialogContent className="flex h-[92vh] w-[96vw] max-w-none flex-col gap-2 p-2 sm:max-w-none">
          <DialogTitle className="sr-only">{selectedLabel ?? t`Computer`}</DialogTitle>
          {screenUrl ? (
            <iframe
              key={`enlarged:${selectedBotId ?? ""}:${screenFrameKey}`}
              ref={enlargedFrameRef}
              title={selectedLabel ?? t`Computer`}
              src={screenUrl}
              sandbox={screenIframeSandbox(screenUrl)}
              className="min-h-0 w-full flex-1 border-0 bg-black"
              allow="clipboard-read; clipboard-write"
              style={{ pointerEvents: "none" }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
